import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { ConditionEditor, useSectionSettings } from '@/features/quote-and-apply-builder';

interface SectionSettingsDialogProps {
  open: boolean;
  section: PolicyFormSection;
  questions: Record<string, PolicyFormQuestion>;
  onSave: (updates: Partial<PolicyFormSection>) => void;
  onClose: () => void;
}

export function SectionSettingsDialog({
  open,
  section,
  questions,
  onSave,
  onClose,
}: SectionSettingsDialogProps) {
  const {
    title,
    setTitle,
    hasCondition,
    setHasCondition,
    condition,
    setCondition,
    handleSave,
  } = useSectionSettings({ section, questions, onSave, onClose });

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent className="max-h-[95vh] gap-6 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Section Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="section-title">Section Title</Label>
            <Input
              value={title}
              onChange={event => setTitle(event.target.value)}
              placeholder="Enter section title"
              id="section-title"
            />
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="show-hide-rule">Show/Hide Rule</Label>
              <Select
                value={hasCondition ? 'yes' : 'no'}
                onValueChange={value => setHasCondition(value === 'yes')}
              >
                <SelectTrigger
                  id="show-hide-rule"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Always Show</SelectItem>
                  <SelectItem value="yes">Conditional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasCondition && (
              <div className="space-y-3 rounded-lg border p-4">
                <p className="text-sm text-gray-500">
                  Define when this section should be visible.
                </p>
                <ConditionEditor
                  condition={condition}
                  questions={questions}
                  onChange={setCondition}
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
